import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Plus, RefreshCw, Search, Filter, AlertTriangle, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { apqpService, clientService, productService } from '../services/api';

const STATUSES = [
  'Da pianificare', 'Pianificato', 'In corso', 'In attesa del cliente', 'In attesa del fornitore',
  'Sospeso', 'In ritardo', 'Campionatura spedita', 'PPAP inviato', 'Approvato', 'Chiuso', 'Annullato',
];
const STATUS_COLOR = {
  'Da pianificare': 'bg-slate-100 text-slate-700 border-slate-300',
  'Pianificato': 'bg-blue-100 text-blue-700 border-blue-300',
  'In corso': 'bg-sky-100 text-sky-800 border-sky-300',
  'In attesa del cliente': 'bg-purple-100 text-purple-800 border-purple-300',
  'In attesa del fornitore': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'Sospeso': 'bg-neutral-200 text-neutral-800 border-neutral-400',
  'In ritardo': 'bg-red-100 text-red-800 border-red-300',
  'Campionatura spedita': 'bg-teal-100 text-teal-800 border-teal-300',
  'PPAP inviato': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  'Approvato': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Chiuso': 'bg-slate-200 text-slate-700 border-slate-400',
  'Annullato': 'bg-rose-100 text-rose-800 border-rose-300',
};
const PROJECT_TYPES = ['APQP', 'PPAP', 'APQP + PPAP'];
const PPAP_LEVELS = ['1', '2', '3', '4', '5'];

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('it-IT') : '—';

export default function ApqpProjectsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [filters, setFilters] = useState({
    q: '', status: '', client_id: '', project_type: '', only_open: true, only_late: false,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', client_id: '', product_id: '', product_code: '', product_description: '',
    drawing_code: '', drawing_revision: '', ppap_quantity: '', project_type: 'APQP + PPAP',
    ppap_level: '3', internal_manager: '', customer_contact: '',
    customer_requested_date: '', planned_sampling_date: '', planned_documentation_date: '', planned_approval_date: '',
    notes: '', template_id: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      if (filters.client_id) params.client_id = filters.client_id;
      if (filters.project_type) params.project_type = filters.project_type;
      if (filters.only_open) params.only_open = true;
      if (filters.only_late) params.only_late = true;
      const r = await apqpService.listProjects(params);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento progetti');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    clientService.getAll({ limit: 500 }).then(r => setClients(r.data?.items || r.data || []));
    productService.getAll({ limit: 500 }).then(r => setProducts(r.data?.items || r.data || []));
    apqpService.listTemplates().then(r => setTemplates(r.data || []));
  }, []);

  const onSelectProduct = (pid) => {
    const p = products.find(x => x.id === pid);
    setForm(f => ({
      ...f,
      product_id: pid,
      product_code: p?.code || f.product_code,
      product_description: p?.name || f.product_description,
      client_id: p?.client_id || f.client_id,
    }));
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Titolo obbligatorio'); return; }
    try {
      const payload = { ...form };
      if (!payload.ppap_quantity) delete payload.ppap_quantity;
      else payload.ppap_quantity = Number(payload.ppap_quantity);
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      const r = await apqpService.createProject(payload);
      toast.success(`Progetto ${r.data.project_number} creato`);
      setDialogOpen(false);
      navigate(`/apqp-projects/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione progetto');
    }
  };

  const stats = useMemo(() => {
    const total = items.length;
    const late = items.filter(p => (p.late_days || 0) > 0 || p.status === 'In ritardo').length;
    const inProgress = items.filter(p => p.status === 'In corso').length;
    const waiting = items.filter(p => (p.status || '').startsWith('In attesa')).length;
    return { total, late, inProgress, waiting };
  }, [items]);

  return (
    <>
      <div className="space-y-4" data-testid="apqp-projects-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Progetti APQP/PPAP</h1>
              <p className="text-sm text-slate-500">Pianificazione campionature e Advanced Product Quality Planning</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading} data-testid="reload-apqp-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={() => setDialogOpen(true)} data-testid="new-apqp-project-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuovo Progetto
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Progetti aperti" value={stats.total} color="bg-slate-50 border-slate-200" />
          <StatCard label="In corso" value={stats.inProgress} color="bg-sky-50 border-sky-200" />
          <StatCard label="In attesa" value={stats.waiting} color="bg-purple-50 border-purple-200" />
          <StatCard label="In ritardo" value={stats.late} color="bg-red-50 border-red-200" iconColor="text-red-600" />
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs text-slate-500"><Filter className="w-3.5 h-3.5" /> Filtri</div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    placeholder="Cerca per titolo, prodotto, cliente, numero…"
                    value={filters.q}
                    onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
                    className="pl-8 h-8 text-xs"
                    data-testid="apqp-search"
                  />
                </div>
              </div>
              <Select value={filters.status || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="apqp-filter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.client_id || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, client_id: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Cliente" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i clienti</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.project_type || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, project_type: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  {PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-3 text-xs text-slate-600">
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={filters.only_open} onChange={(e) => setFilters(f => ({ ...f, only_open: e.target.checked }))} />
                  Solo aperti
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={filters.only_late} onChange={(e) => setFilters(f => ({ ...f, only_late: e.target.checked }))} data-testid="apqp-filter-late" />
                  Solo in ritardo
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-slate-600">N.</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Titolo · Prodotto</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Cliente</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Tipo</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Responsabile</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Campionatura</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Approvazione</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-center">Avanz.</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Stato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.length === 0 && !loading && (
                    <tr><td colSpan={10} className="text-center text-slate-400 py-8">Nessun progetto.</td></tr>
                  )}
                  {items.map(p => (
                    <tr
                      key={p.id}
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/apqp-projects/${p.id}`)}
                      data-testid={`apqp-row-${p.id}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs font-semibold">{p.project_number}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{p.title}</div>
                        <div className="text-xs text-slate-500">
                          {p.product_code ? <span className="font-mono">{p.product_code}</span> : '—'}
                          {p.product_description && <span> · {p.product_description}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">{p.client_name || '—'}</td>
                      <td className="px-3 py-2"><Badge className="bg-slate-100 text-slate-700 border-slate-300 border text-[10px]">{p.project_type}</Badge></td>
                      <td className="px-3 py-2 text-xs">{p.internal_manager || '—'}</td>
                      <td className="px-3 py-2 text-xs font-mono">{fmt(p.planned_sampling_date)}</td>
                      <td className="px-3 py-2 text-xs font-mono">{fmt(p.planned_approval_date)}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-1">
                          <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: `${p.progress_percent || 0}%` }} />
                          </div>
                          <span className="text-[10px] text-slate-500 font-mono">{p.progress_percent || 0}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-1 items-start">
                          <Badge className={`${STATUS_COLOR[p.status] || 'bg-slate-100'} border text-[10px]`}>{p.status}</Badge>
                          {(p.late_days || 0) > 0 && (
                            <Badge className="bg-red-100 text-red-800 border border-red-300 text-[10px]">
                              <AlertTriangle className="w-3 h-3 mr-0.5 inline" /> +{p.late_days}gg
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right"><ChevronRight className="w-4 h-4 text-slate-400 inline" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New project dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="apqp-new-dialog">
          <DialogHeader><DialogTitle>Nuovo Progetto APQP/PPAP</DialogTitle><DialogDescription>Inserisci i dati principali del progetto. Il numero verrà generato automaticamente.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-3">
              <Label>Titolo *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="apqp-title-input" />
            </div>
            <div>
              <Label>Prodotto ERP</Label>
              <Select value={form.product_id || 'none'} onValueChange={(v) => v === 'none' ? setForm(f => ({ ...f, product_id: '' })) : onSelectProduct(v)}>
                <SelectTrigger data-testid="apqp-product-select"><SelectValue placeholder="Seleziona prodotto…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nessuno (nuovo prodotto) —</SelectItem>
                  {products.map(p => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Codice prodotto</Label>
              <Input value={form.product_code} onChange={e => setForm(f => ({ ...f, product_code: e.target.value }))} />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Input value={form.product_description} onChange={e => setForm(f => ({ ...f, product_description: e.target.value }))} />
            </div>
            <div>
              <Label>Cliente</Label>
              <Select value={form.client_id || 'none'} onValueChange={(v) => setForm(f => ({ ...f, client_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="apqp-client-select"><SelectValue placeholder="Seleziona cliente…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nessuno —</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Disegno</Label>
              <Input value={form.drawing_code} onChange={e => setForm(f => ({ ...f, drawing_code: e.target.value }))} />
            </div>
            <div>
              <Label>Revisione</Label>
              <Input value={form.drawing_revision} onChange={e => setForm(f => ({ ...f, drawing_revision: e.target.value }))} />
            </div>
            <div>
              <Label>Q.tà campionatura</Label>
              <Input type="number" value={form.ppap_quantity} onChange={e => setForm(f => ({ ...f, ppap_quantity: e.target.value }))} />
            </div>
            <div>
              <Label>Tipo progetto</Label>
              <Select value={form.project_type} onValueChange={(v) => setForm(f => ({ ...f, project_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Livello PPAP</Label>
              <Select value={form.ppap_level} onValueChange={(v) => setForm(f => ({ ...f, ppap_level: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PPAP_LEVELS.map(l => <SelectItem key={l} value={l}>Livello {l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Responsabile interno</Label>
              <Input value={form.internal_manager} onChange={e => setForm(f => ({ ...f, internal_manager: e.target.value }))} />
            </div>
            <div>
              <Label>Referente cliente</Label>
              <Input value={form.customer_contact} onChange={e => setForm(f => ({ ...f, customer_contact: e.target.value }))} />
            </div>
            <div>
              <Label>Data richiesta cliente</Label>
              <Input type="date" value={form.customer_requested_date} onChange={e => setForm(f => ({ ...f, customer_requested_date: e.target.value }))} />
            </div>
            <div>
              <Label>Data campionatura</Label>
              <Input type="date" value={form.planned_sampling_date} onChange={e => setForm(f => ({ ...f, planned_sampling_date: e.target.value }))} />
            </div>
            <div>
              <Label>Data invio PPAP</Label>
              <Input type="date" value={form.planned_documentation_date} onChange={e => setForm(f => ({ ...f, planned_documentation_date: e.target.value }))} />
            </div>
            <div>
              <Label>Data approvazione PPAP</Label>
              <Input type="date" value={form.planned_approval_date} onChange={e => setForm(f => ({ ...f, planned_approval_date: e.target.value }))} />
            </div>
            <div className="md:col-span-3">
              <Label>Modello di partenza (opzionale)</Label>
              <Select value={form.template_id || 'none'} onValueChange={(v) => setForm(f => ({ ...f, template_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="apqp-template-select"><SelectValue placeholder="Nessun modello (Gantt vuoto)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nessun modello —</SelectItem>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} · {t.tasks?.length || 0} attività
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Il modello genera automaticamente le attività del Gantt.</p>
            </div>
            <div className="md:col-span-3">
              <Label>Note</Label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={submit} data-testid="apqp-create-submit">Crea progetto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const StatCard = ({ label, value, color, iconColor }) => (
  <div className={`border rounded-lg p-3 ${color}`}>
    <div className="text-xs uppercase text-slate-500">{label}</div>
    <div className={`text-2xl font-bold ${iconColor || 'text-slate-900'}`}>{value}</div>
  </div>
);
